/**
 * Payload schemas for the second batch of non-forwarded events that had none (harness to nine B5c,
 * P1 item 3): what the server tells the web about the computer (presence, credits, capabilities,
 * the browser view), the web's browser and terminal controls, media jobs, the prewarm path, Nami's
 * capture and deposit frames, and the box's nested tool calls and hook trace.
 *
 * Each schema is written from what its producers send today (every emit site was read and is named
 * on the schema). Where a hand-written type described a payload it is now DERIVED from the schema
 * (one author): Presence, CreditSummary, UserCapabilities, BrowserViewStatePayload,
 * BrowserOpenSessionPayload, BrowserOpenTabPayload, BrowserCloseTabPayload, BrowserStatsPayload,
 * TerminalExecPayload, TerminalResultPayload. Their named sub-types (UpgradeProgress, SandboxRepair,
 * BrowserViewStreamCreds and the like) are indexed out of the derived types, so nothing is written twice.
 *
 * A frame some producer sends with no argument at all (a pure signal) is `z.object({}).strict()`,
 * optional: the server's own forward of it is `{}`.
 */
import { z } from 'zod';
import { HARNESS_IDS } from '../harness/ids.js';
import type { BackgroundAgentSource } from '../types/credits.js';
import { NAMI_EPISTEMICS, NAMI_SOURCES } from '../types/nami.js';
import { PRESENCE_PHASES } from '../types/presence.js';
import { TURN_OUTCOME_KINDS } from '../types/turn-outcome.js';

// ============================================================================
// Presence (server → web)
// ============================================================================

/**
 * presence:update: one read of the computer's state (types/presence.ts Presence derives from this).
 * apps/server presence.service.ts getCurrentPresence, sent by recomputeAndBroadcast (on change),
 * handlers/socket/session-handlers.ts (connect, connection:state:query) and task-handlers.ts.
 * The markers ride `true` or not at all; each phase-specific field only with its phase.
 */
export const PresenceSchema = z.object({
  phase: z.enum(PRESENCE_PHASES),
  /** 0..N, updated atomically with phase. */
  activeTasks: z.number(),
  /** True while a browser session is open or pending. */
  hasBrowserSession: z.boolean().optional(),
  /**
   * Someone is looking at a hosted app right now (an `apps:view` heartbeat
   * inside its 15s TTL). Same orthogonal activity-marker contract as
   * `hasBrowserSession`: not a phase. `phase` describes the AGENT lane and an
   * apps-only wake never dials the runner, so without this field a served,
   * watched app reads 'hibernating' — the badge said Standby while the app was
   * on screen (founder, 2026-08-29). TTL-derived at read time; cannot go stale.
   */
  hasAppActivity: z.boolean().optional(),
  /** Only populated when phase === 'upgrading'. `startedAt` is ISO 8601. */
  upgradeProgress: z.object({ step: z.enum(['pulling', 'starting', 'ready']), startedAt: z.string() }).optional(),
  /** Only populated when phase === 'error'. */
  error: z.object({ code: z.string(), message: z.string(), retryable: z.boolean() }).optional(),
  /**
   * A WALL, not a failure (founder 09-04): the computer stayed asleep because a
   * wake door refused — today only the credit bar. Rides `hibernating` (the box
   * IS asleep, truthfully) so the dot stays calm and nothing reads "broken";
   * `error` is reserved for things that are broken. TTL-bounded, cleared by
   * the next successful wake, and never present while a relay is live.
   */
  wall: z.literal('out_of_credits').optional(),
  /**
   * Housekeeping the SERVER is doing to the machine right now — restarting it from
   * its image, growing it, sweeping it (sandbox storage by lifetime, spec §6.3).
   * Same contract as `wall`: orthogonal to `phase`, never a phase of its own. It
   * swaps the pane's SUBTITLE and nothing else, because a repair IS the phase the box
   * is already in — a restart from the image is an update, a grow is an update.
   * TTL-bounded server-side and dropped once the box is `ready` again, so it cannot
   * latch; absent on the overwhelming majority of beats, like every field above.
   * `start_queued` and `start_held` (box hosts, Plan 3c): boat refused to start the
   * machine, for the minute (it waits its turn and starts on its own) or for longer
   * (it holds until boat has room). The person's work is saved either way.
   */
  repair: z
    .object({
      action: z.enum(['restart_from_image', 'memory_grow', 'tidying', 'unreachable', 'start_queued', 'start_held']),
      reason: z.string().optional(),
      /** Server clock, ms. */
      at: z.number(),
    })
    .optional(),
});

/** connection:state:query (web → server): ask for a presence:update now. Sent with no payload. */
export const ConnectionStateQueryPayloadSchema = z.object({}).strict().optional();

/**
 * server:handover (server → web): at a deploy the old server hands its own tabs to its successor.
 * Sent only to that server's tabs (socket.io `.local`), immediately before it disconnects them, so
 * the disconnect that follows is a move and not a failure. apps/server tab-handover.ts
 * (handOverLocalTabs: the drain's per-user shed and the 4010 branch; handOverAllLocalTabs: the
 * final sweep).
 */
export const ServerHandoverPayloadSchema = z.object({
  reason: z.literal('deploy'),
  /** Server clock, ms. */
  timestamp: z.number(),
});

// ============================================================================
// Credits and capabilities (server → web)
// ============================================================================

/** Every source, keyed by itself: a source added to (or dropped from) the type fails the shared build here. */
const BACKGROUND_AGENT_SOURCES = {
  curator: 'curator',
  contradiction: 'contradiction',
  memory_cards: 'memory_cards',
  wiki: 'wiki',
  skills: 'skills',
  brief: 'brief',
  prep_pack: 'prep_pack',
  proposal_judge: 'proposal_judge',
  session_summary: 'session_summary',
  title_generation: 'title_generation',
  notch_summary: 'notch_summary',
  os_ground: 'os_ground',
  routine_scope_gate: 'routine_scope_gate',
  injection_screen: 'injection_screen',
  done_verdict: 'done_verdict',
  delivery_voice: 'delivery_voice',
  sense_gist: 'sense_gist',
  slack_intro: 'slack_intro',
} as const satisfies { [K in BackgroundAgentSource]: K };

/**
 * credit:summary: the person's credit summary (types/credits.ts CreditSummary derives from this).
 * apps/server credit.service.ts getUserCreditSummary, sent by handlers/socket/orchestrator.ts (on
 * connect), credit-stream.service.ts (debounced on each charge) and handlers/credit-routes.handler.ts.
 * The same shape answers GET /api/credits.
 */
export const CreditSummarySchema = z.object({
  balance: z.number(),
  lifetimeSpent: z.number(),
  lifetimeEarned: z.number(),
  lastRechargeAmount: z.number(),
  spentSinceRecharge: z.number(),
  priorBalance: z.number(),
  todaySpent: z.number(),
  weekSpent: z.number(),
  /**
   * The last 7 days INCLUDING today, ascending, one entry per day even when nothing was spent
   * on it. Dates are UTC days (`YYYY-MM-DD`) on the same boundary `todaySpent` uses, so the
   * last entry's `spent` EQUALS `todaySpent`. Optional: a server whose
   * `get_user_credit_summary` predates the week_by_day leg simply omits it, and consumers
   * draw nothing rather than guessing.
   */
  weekByDay: z.array(z.object({ date: z.string(), spent: z.number() })).optional(),
  todayIntelligence: z.number(),
  todayActions: z.number(),
  todayCompute: z.number(),
  weekIntelligence: z.number(),
  weekActions: z.number(),
  weekCompute: z.number(),
  /**
   * All-time per-category spend (DB-side SUM grouped by ledger type, cap-safe). Drives the
   * COST BREAKDOWN bar so all four categories share ONE window (all-time) — Background is
   * already all-time, so this makes Intelligence/Actions/Compute match instead of being a
   * recent-task-page slice. Optional + back-compatible: consumers default to 0.
   */
  allTimeIntelligence: z.number().optional(),
  allTimeActions: z.number().optional(),
  allTimeCompute: z.number().optional(),
  /** All-time spend on any deduction type NOT in the 4 named categories (e.g. memory_extraction)
   *  + any unrecognized background source. Keeps the breakdown reconciling to the all-time total. */
  allTimeOther: z.number().optional(),
  /** All-time spend on the composer itself, split in two: `composerTyped` is the
   *  typed turns (`token_usage` rows tagged `metadata.lane = 'composer'`),
   *  `composerVoice` is the minutes on the Line (`tool_usage` rows tagged
   *  `metadata.connector_source = 'voice_live'`). The SQL function carves both
   *  OUT of allTimeIntelligence / allTimeActions, so the categories still sum to
   *  all-time spend. Optional + back-compatible: a server whose function predates
   *  the legs omits them, consumers read 0, and the composer row simply is not
   *  drawn. */
  composerTyped: z.number().optional(),
  composerVoice: z.number().optional(),
  /**
   * Total credits spent on nami background-agent Haiku work (type='background_agent').
   * Optional + back-compatible: existing consumers default it to 0.
   */
  backgroundAgentSpend: z.number().optional(),
  /**
   * Background spend broken down by source (curator/contradiction/memory_cards/
   * wiki/skills/brief). Optional; when present the server sends every source (credit.service.ts).
   */
  backgroundBySource: z.record(z.enum(BACKGROUND_AGENT_SOURCES), z.number()).optional(),
  isGracePeriod: z.boolean(),
  graceExpiresAt: z.string().nullable(),
  settings: z.object({
    warnBalance: z.number(),
    confirmToolCost: z.number(),
    confirmTaskCost: z.number(),
    /** The user's own per-turn spend ceiling in USD; null = the platform default
     *  governs. Optional: a server whose `get_user_credit_summary` predates the
     *  leg simply omits it. */
    turnSpendCeilingUsd: z.number().nullable().optional(),
    /** Per-TASK lifetime budget from user_credit_settings; null = no per-task limit. */
    maxTaskBudgetUsd: z.number().nullable().optional(),
    /** The user's own daily spend ceiling in USD; null = the platform default
     *  governs. Optional for the same reason as the per-turn leg above. */
    dailySpendCeilingUsd: z.number().nullable().optional(),
  }),
});

/**
 * user:capabilities: what this person may use (types/capabilities.ts UserCapabilities derives from
 * this). handlers/socket/orchestrator.ts once per connection (false on the fail-closed path).
 */
export const UserCapabilitiesSchema = z.object({
  hasBrowserStreaming: z.boolean(),
});

// ============================================================================
// The browser view and session (server ↔ web, server ↔ runner)
// ============================================================================

const BrowserViewStreamCredsSchema = z.object({
  sessionId: z.string(),
  nekoUrl: z.string(),
  previewToken: z.string().optional(),
  /** The query parameter previewToken rides in: `_token` on boat; unset on Blaxel, read as `bl_preview_token`. */
  previewTokenParam: z.string().optional(),
  /** Never empty in a 'live' snapshot: a mint failure is state 'error'. */
  nekoToken: z.string(),
  iceServers: z.array(z.object({ urls: z.array(z.string()), username: z.string().optional(), credential: z.string().optional() })),
  width: z.number(),
  height: z.number(),
});

/**
 * browser:view_state (server → web): the browser view machine's snapshot
 * (types/browser-view.ts BrowserViewStatePayload derives from this). relay/browser-view-machine.ts,
 * to the room on each recompute and to one socket on a pull or connect (BROWSER_VIEW_MACHINE_V1).
 */
export const BrowserViewStatePayloadSchema = z.object({
  state: z.enum(['hidden', 'waking', 'live', 'suspended', 'error']),
  /** Epoch ms of the last transition; clients ignore stale (<= seen) snapshots (0 when rebuilt for a missing record). */
  since: z.number(),
  /** Only with 'live' and 'waking' (waking may pre-ship creds when cached). */
  streamCreds: BrowserViewStreamCredsSchema.optional(),
  error: z
    .object({
      /** `out_of_credits`: the wake-door credit gate (founder 09-03); the balance is exhausted, so the
       *  box was NOT woken. The pane walls with Add credits, not Reconnect. */
      code: z.enum(['wake_timeout', 'mint_failed', 'no_access', 'stream_lost', 'out_of_credits']),
      message: z.string(),
    })
    .optional(),
});

/** browser:wake (web → server): wake the browser, or the whole computer. Usually sent with no payload. */
export const BrowserWakePayloadSchema = z.object({ target: z.literal('computer').optional() }).strict().optional();

/**
 * computer:sound (web → server, server-terminal): is sound from the computer playing right now. The
 * web's ComputerSoundPlayer beats `true` while it hears sound, with `idleMs` (how long since the
 * person's last input or navigation in the app; the server refuses a beat at an hour), and says
 * `false` when it stops (types/browser-stream.ts ComputerSoundPayload derives from this).
 */
export const ComputerSoundPayloadSchema = z.object({
  playing: z.boolean(),
  idleMs: z.number().finite().nonnegative().optional(),
});

/**
 * browser:open_session: the web's ask (web → server; `origin: 'agent'` when an agent opened it) and
 * the server's open (server → runner; `wakeGrant` when a wake grant rides it). One name, both legs
 * (types/browser-stream.ts BrowserOpenSessionPayload derives from this).
 */
export const BrowserOpenSessionPayloadSchema = z.object({
  origin: z.literal('agent').optional(),
  wakeGrant: z.boolean().optional(),
});

/** browser:close_session (web → server): close the browser pane. Always `{}`. */
export const BrowserCloseSessionPayloadSchema = z.object({}).strict();

/** browser:open_tab (web → server → runner; types/browser-stream.ts BrowserOpenTabPayload derives from this). No live caller today. */
export const BrowserOpenTabPayloadSchema = z.object({
  url: z.string().optional(),
});

/** browser:close_tab (web → server → runner; types/browser-stream.ts BrowserCloseTabPayload derives from this). No live caller today. */
export const BrowserCloseTabPayloadSchema = z.object({
  targetId: z.string(),
});

/** browser:session_started (runner → server): browser/browser-session.ts on the neko transport, once per session. */
export const BrowserSessionStartedPayloadSchema = z.object({
  sessionId: z.string(),
});

/**
 * browser:session_stopped (runner → server): browser/browser-session.ts on close or idle, and
 * display-guard.ts when the display is empty.
 */
export const BrowserSessionStoppedPayloadSchema = z.object({
  sessionId: z.string(),
  reason: z.enum(['closed', 'idle', 'display-empty']),
});

/**
 * browser:stats (web → server): one WebRTC sample every 5 s per stream (apps/web hooks/webrtc-stats.ts
 * via useNekoClient.ts; types/browser-stats.ts BrowserStatsPayload derives from this).
 */
export const BrowserStatsPayloadSchema = z.object({
  /** Sample timestamp, ms since epoch on the client. */
  ts: z.number(),
  /** Inbound video bitrate as observed by the receiver, in kilobits per second, smoothed over the last 5s. */
  bitrate_kbps: z.number(),
  /** Packet loss percentage on the inbound video stream: lost / received over the window, so it can pass 100. */
  packets_lost_pct: z.number(),
  /** Currently selected ICE candidate-pair RTT in milliseconds. */
  rtt_ms: z.number(),
  /** Frames per second the client has decoded over the last 5s. */
  frames_per_second: z.number(),
  /** Percentage of received frames that decoded successfully (0-100). */
  frames_decoded_pct: z.number(),
  /** Inbound video jitter in milliseconds. */
  jitter_ms: z.number(),
  /** Width and height of the most recent decoded frame. */
  resolution: z.object({ width: z.number(), height: z.number() }),
  /**
   * Active video codec, lowercased mimeType from RTCRtpCodecParameters.
   * 'unknown' when no inbound video stream has negotiated yet (e.g. during
   * ICE setup) or when the codec entry was missing from the stats report.
   */
  codec: z.enum(['video/h264', 'video/vp8', 'video/vp9', 'video/av1', 'video/h265', 'unknown']),
});

/**
 * browser:user_input: a person is driving the browser (web → server, sent with no argument, about
 * once a second; server → runner as `{}` to keep the box awake).
 */
export const BrowserUserInputPayloadSchema = z.object({}).strict().optional();

// ============================================================================
// Terminal (web ↔ server)
// ============================================================================

/** terminal:exec (web → server): stores/terminalStore.ts (types/terminal.ts TerminalExecPayload derives from this). */
export const TerminalExecPayloadSchema = z.object({
  /** Client-generated UUID, echoed back in the result's `id` so the frontend can match the reply to its scrollback entry. */
  id: z.string(),
  /** Raw command string, e.g. `ls -la`. The server passes it to Blaxel sandbox.process.exec as a single string. */
  command: z.string(),
});

/** terminal:result (server → web): handlers/socket/terminal-handlers.ts (types/terminal.ts TerminalResultPayload derives from this). */
export const TerminalResultPayloadSchema = z.object({
  id: z.string(),
  /** Combined stdout + stderr (Blaxel `result.logs`), possibly truncated (see `truncated`). */
  output: z.string(),
  /** Process exit code. -1 sentinels an execution error (sandbox unavailable, network, busy); `error` is populated then. */
  exitCode: z.number(),
  /** Wall-clock ms from receipt of TERMINAL_EXEC to result emit. */
  durationMs: z.number(),
  /** True if the server cut `output` at the 1 MiB cap. */
  truncated: z.boolean(),
  /** Working directory after this command: the server tracks the shell cwd so `cd` persists across execs. */
  cwd: z.string().optional(),
  /** Present when the command could not be run (not when it ran and returned non-zero). User-facing, no stack traces. */
  error: z.string().optional(),
});

// ============================================================================
// Media jobs (server → web)
// ============================================================================

/**
 * job_ready: a media, studio or content job finished. media-completion.service.ts,
 * handlers/fal-webhook-routes.handler.ts (no task_id) and studio/complete.ts. `result` is the
 * job's result object (a UniversalResult, or studio's `{ kind, url }`), passed through.
 */
export const JobReadyPayloadSchema = z.object({
  jobId: z.string(),
  task_id: z.string().nullable().optional(),
  request_id: z.string(),
  messageId: z.string().nullable(),
  blockId: z.string().nullable(),
  result: z.object({ kind: z.string() }).passthrough(),
});

/** job_error: a job failed (media-completion.service.ts, studio/complete.ts, fal-webhook-routes.handler.ts). */
export const JobErrorPayloadSchema = z.object({
  jobId: z.string(),
  task_id: z.string().nullable().optional(),
  request_id: z.string(),
  messageId: z.string().nullable(),
  blockId: z.string().nullable(),
  brief: z.string().nullable().optional(),
  refunded: z.boolean().optional(),
  error: z.string(),
});

/** job_progress: a media job is generating (media-completion.service.ts). */
export const JobProgressPayloadSchema = z.object({
  jobId: z.string(),
  task_id: z.string().nullable(),
  request_id: z.string().nullable(),
  blockId: z.string().nullable(),
  phase: z.literal('generating'),
});

// ============================================================================
// Prewarm and typing (web → server → runner)
// ============================================================================

/** sandbox:pre_warm (web → server): hooks/useTaskPrewarm.ts (`{ taskId, agentSlug }`) and useComposerPrewarm.ts (`{}`). */
export const SandboxPreWarmPayloadSchema = z.object({
  taskId: z.string().optional(),
  agentSlug: z.string().optional(),
});

/** sandbox:typing: the person is typing (web → server with no payload; server → runner as `{}`). */
export const SandboxTypingPayloadSchema = z.object({}).strict().optional();

/** sandbox:prewarm_query (server → runner): sandbox-relay.service.ts, warm a query for a task the person is about to send. */
export const SandboxPrewarmQueryPayloadSchema = z.object({
  taskId: z.string(),
  agentPrompt: z.string().optional(),
  maxBudgetUsd: z.number().optional(),
  runtime: z.enum(HARNESS_IDS).optional(),
});

/** sandbox:warmup_query_ready (runner → server): stream/actor-lifecycle.ts, the warm query is up. */
export const SandboxWarmupQueryReadyPayloadSchema = z.object({
  taskId: z.string(),
  durationMs: z.number(),
});

// ============================================================================
// Nami (runner → server)
// ============================================================================

/**
 * nami:capture_signal: a turn worth remembering (agent-sdk engine/capture-signal-emit.ts, at most
 * once per turn, NAMI_CAPTURE_SIGNAL). `outcomeKind` is the turn's TurnOutcome kind.
 */
export const NamiCaptureSignalPayloadSchema = z.object({
  taskId: z.string(),
  surprise: z.object({
    correction: z.boolean(),
    outcomeKind: z.enum(TURN_OUTCOME_KINDS),
    forcing: z.boolean(),
    score: z.number(),
  }),
});

/**
 * nami:remember_deposit: one fact for Nami, from the remember tool
 * (apps/sandbox harness/tools/remember-tool.ts) or the off-loop sweep (agent-sdk memory-sweep.ts
 * depositEventData). A runner on an older bundle may still send the legacy source 'task_firsthand' or none; the
 * server maps those.
 */
export const NamiRememberDepositPayloadSchema = z.object({
  taskId: z.string(),
  source: z.enum(NAMI_SOURCES),
  epistemic: z.enum(NAMI_EPISTEMICS),
  observation: z.string(),
  quote: z.string().optional(),
});

// ============================================================================
// The box's own trace (runner → server)
// ============================================================================

/**
 * agent:nested_tool_call: one tool call made from inside execute_code (agent-sdk
 * tools/code-exec/execute-code-tool.ts, a raw harness event). The tool's context names no task, so
 * the runner's sink stamps the task (apps/sandbox stream/harness-sink.ts) and the relay files the
 * call under that task's trace.
 */
export const NestedToolCallPayloadSchema = z.object({
  taskId: z.string(),
  parentToolUseId: z.string().optional(),
  toolUseId: z.string(),
  toolName: z.string(),
  success: z.boolean(),
  durationMs: z.number(),
});

/**
 * agent:hook_execution: a hook callback's lifecycle (apps/sandbox stream/harness-sink.ts, only when
 * INCLUDE_HOOK_EVENTS=1). The terminal phases carry the duration; the error phase its message.
 */
export const HookExecutionPayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string(),
  hookId: z.string(),
  hookEvent: z.string(),
  phase: z.enum(['started', 'completed', 'error', 'cancelled']),
  matcher: z.string().optional(),
  durationMs: z.number().optional(),
  error: z.string().optional(),
  hookName: z.string().optional(),
  stdout: z.string().optional(),
  stderr: z.string().optional(),
});

/**
 * security:blocked (runner → server): apps/sandbox hooks/security-hooks.ts when a command or a
 * file is refused. Those hooks are wired only through createHooksConfig, which no live path calls.
 */
export const SecurityBlockedPayloadSchema = z.object({
  toolName: z.string(),
  toolUseId: z.string().optional(),
  reason: z.string(),
  pattern: z.string().optional(),
  command: z.string().optional(),
  filePath: z.string().optional(),
  sessionId: z.string(),
});

/**
 * agent:notification (runner → server): a notification hook's message (apps/sandbox hooks/index.ts
 * and agent-sdk native-hooks.ts routeNotification). Neither is reached by a live path today.
 */
export const AgentNotificationPayloadSchema = z.object({
  taskId: z.string(),
  type: z.string(),
  title: z.string().optional(),
  message: z.string(),
});

